/**
 * Los iconos de la navegacion, dibujados.
 *
 * ANTES ERAN CARACTERES: ▦ ◔ ⌂ ⊞ ☑ ◆ € ▣ ✓ ↗ ✦ ☺ ⚒ ⚙. Cada uno venia de una
 * familia distinta y con un grosor distinto, algunos son emoji en ciertos
 * sistemas —el ☺ y el ⚒ salen a todo color en Windows— y el navegador los
 * sustituye por la fuente que tenga a mano, asi que el menu se veia distinto en
 * cada maquina. Es lo que hace que una barra lateral parezca un boceto.
 *
 * Aqui son SVG de un solo trazo, 20x20, en `currentColor` y con el mismo
 * grosor: heredan el color del estado del menu —apagado, hover, activo— sin que
 * haya que pintarlos, y se ven igual en todas partes.
 */

export type NombreIcono =
  | 'mas'
  | 'panel'
  | 'alerta'
  | 'casa'
  | 'calendario'
  | 'checklist'
  | 'embudo'
  | 'euro'
  | 'reloj'
  | 'tareas'
  | 'compartir'
  | 'chispa'
  | 'personas'
  | 'llave-inglesa'
  | 'ajustes'
  | 'importar'
  | 'sobre'
  | 'menu'
  | 'ojo'
  | 'ojo-tachado'
  | 'cerrar'
  | 'aviso'
  | 'cronometro'
  | 'camara'
  | 'documento'
  | 'clip'
  | 'microfono'
  | 'factura'
  | 'candado'
  | 'enchufe'
  | 'paleta'
  | 'etiqueta'
  | 'edificio'
  | 'papelera'
  | 'lapiz'
  | 'comentario'
  | 'prohibido'
  | 'enviar'
  | 'descargar'
  | 'repetir'
  | 'pin'
  | 'reproducir'
  | 'billete'
  | 'comprobado'
  | 'volver';

/*
  Cada entrada es el contenido del `<svg>`: se dibuja sobre una rejilla de 20x20
  con el trazo ya fijado fuera, para que ningun icono pueda desviarse del
  grosor comun.
*/
const TRAZOS: Record<NombreIcono, React.ReactNode> = {
  mas: <path d="M10 4.5v11M4.5 10h11" />,
  panel: (
    <>
      <rect x="3" y="3" width="6.2" height="6.2" rx="1.2" />
      <rect x="10.8" y="3" width="6.2" height="6.2" rx="1.2" />
      <rect x="3" y="10.8" width="6.2" height="6.2" rx="1.2" />
      <rect x="10.8" y="10.8" width="6.2" height="6.2" rx="1.2" />
    </>
  ),
  alerta: (
    <>
      <path d="M5.4 8.2a4.6 4.6 0 0 1 9.2 0c0 3.4 1.3 4.6 1.3 4.6H4.1s1.3-1.2 1.3-4.6Z" />
      <path d="M8.4 15.4a1.9 1.9 0 0 0 3.2 0" />
    </>
  ),
  casa: (
    <>
      <path d="M3.2 8.6 10 3.2l6.8 5.4" />
      <path d="M4.8 8v8.4h10.4V8" />
      <path d="M8.2 16.4v-4.2h3.6v4.2" />
    </>
  ),
  calendario: (
    <>
      <rect x="3" y="4.4" width="14" height="12.2" rx="1.6" />
      <path d="M3 8.4h14M7 3v2.8M13 3v2.8" />
    </>
  ),
  checklist: (
    <>
      <rect x="3.4" y="3.4" width="13.2" height="13.2" rx="2" />
      <path d="m6.8 10.2 2.3 2.3 4.1-4.6" />
    </>
  ),
  embudo: <path d="M3.4 4.2h13.2l-5.1 6v5.6l-3-1.6v-4L3.4 4.2Z" />,
  euro: (
    <>
      <path d="M14.4 5.6a5.6 5.6 0 1 0 0 8.8" />
      <path d="M3.8 8.6h6.4M3.8 11.4h6.4" />
    </>
  ),
  reloj: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 5.8V10l3 1.8" />
    </>
  ),
  tareas: (
    <>
      <path d="M3.4 5.6 4.8 7l2.4-2.6M3.4 10.4l1.4 1.4 2.4-2.6M3.4 15.2l1.4 1.4L7.2 14" />
      <path d="M9.8 5.8h6.8M9.8 10.6h6.8M9.8 15.4h6.8" />
    </>
  ),
  compartir: (
    <>
      <circle cx="15" cy="5" r="2.2" />
      <circle cx="5" cy="10" r="2.2" />
      <circle cx="15" cy="15" r="2.2" />
      <path d="m7 8.9 6-2.8M7 11.1l6 2.8" />
    </>
  ),
  chispa: (
    <>
      <path d="M10 2.8 11.6 8 16.8 9.6 11.6 11.2 10 16.4 8.4 11.2 3.2 9.6 8.4 8Z" />
      <path d="M15.4 3v2.6M14.1 4.3h2.6" />
    </>
  ),
  personas: (
    <>
      <circle cx="8" cy="7.4" r="2.8" />
      <path d="M3 16.4c0-2.6 2.2-4.4 5-4.4s5 1.8 5 4.4" />
      <path d="M13.4 5.2a2.6 2.6 0 0 1 0 5M14.6 12.4c1.6.6 2.6 2 2.6 4" />
    </>
  ),
  'llave-inglesa': (
    <>
      <path d="M13.4 3.2a3.9 3.9 0 0 0-3.3 5.9L3.4 15.8l1.4 1.4 6.6-6.7a3.9 3.9 0 0 0 5.4-4.6l-2.2 2.2-2.1-.5-.5-2.1 2.2-2.2a3.9 3.9 0 0 0-1.4-.1Z" />
    </>
  ),
  /*
    Rueda dentada de contorno, no una estrella de rayos: con ocho lineas
    saliendo de un circulo, a 20 pixeles se lee «sol» y no «ajustes».
  */
  ajustes: (
    <>
      <circle cx="10" cy="10" r="2.7" />
      <path d="M16.1 12.2a1.4 1.4 0 0 0 .28 1.54l.05.05a1.7 1.7 0 1 1-2.4 2.4l-.05-.05a1.4 1.4 0 0 0-1.54-.28 1.4 1.4 0 0 0-.85 1.28v.14a1.7 1.7 0 0 1-3.4 0v-.07a1.4 1.4 0 0 0-.92-1.28 1.4 1.4 0 0 0-1.54.28l-.05.05a1.7 1.7 0 1 1-2.4-2.4l.05-.05a1.4 1.4 0 0 0 .28-1.54 1.4 1.4 0 0 0-1.28-.85h-.14a1.7 1.7 0 0 1 0-3.4h.07a1.4 1.4 0 0 0 1.28-.92 1.4 1.4 0 0 0-.28-1.54l-.05-.05a1.7 1.7 0 1 1 2.4-2.4l.05.05a1.4 1.4 0 0 0 1.54.28h.07a1.4 1.4 0 0 0 .85-1.28v-.14a1.7 1.7 0 0 1 3.4 0v.07a1.4 1.4 0 0 0 .85 1.28 1.4 1.4 0 0 0 1.54-.28l.05-.05a1.7 1.7 0 1 1 2.4 2.4l-.05.05a1.4 1.4 0 0 0-.28 1.54v.07a1.4 1.4 0 0 0 1.28.85h.14a1.7 1.7 0 0 1 0 3.4h-.07a1.4 1.4 0 0 0-1.28.85Z" />
    </>
  ),
  importar: (
    <>
      <path d="M10 12.6V3.4M6.6 6.8 10 3.4l3.4 3.4" />
      <path d="M3.6 12.4v3a1.6 1.6 0 0 0 1.6 1.6h9.6a1.6 1.6 0 0 0 1.6-1.6v-3" />
    </>
  ),
  sobre: (
    <>
      <rect x="2.8" y="4.6" width="14.4" height="10.8" rx="1.6" />
      <path d="m3.4 5.8 6.6 5 6.6-5" />
    </>
  ),
  menu: <path d="M3.4 6h13.2M3.4 10h13.2M3.4 14h13.2" />,
  cerrar: <path d="m5 5 10 10M15 5 5 15" />,
  aviso: (
    <>
      <path d="M10 2.8 18.4 16.6H1.6L10 2.8Z" />
      <path d="M10 8v3.4M10 14.1h.01" />
    </>
  ),
  cronometro: (
    <>
      <circle cx="10" cy="11.2" r="6.2" />
      <path d="M10 8.2v3l2 1.4M7.8 2.4h4.4M15.4 5.6l1.4-1.4" />
    </>
  ),
  camara: (
    <>
      <path d="M2.6 6.6h3l1.2-2h6.4l1.2 2h3a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2.6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z" />
      <circle cx="10" cy="11.4" r="3" />
    </>
  ),
  documento: (
    <>
      <path d="M4.6 2.6h7l4 4v10.8H4.6V2.6Z" />
      <path d="M11.4 2.6v4.2h4.2M7.2 11h5.6M7.2 13.8h5.6" />
    </>
  ),
  clip: <path d="M15.4 9.4 9.7 15a3.4 3.4 0 1 1-4.8-4.8l6.4-6.4a2.3 2.3 0 0 1 3.2 3.2l-6.4 6.4a1.1 1.1 0 0 1-1.6-1.6l5.7-5.7" />,
  microfono: (
    <>
      <rect x="7.4" y="2.4" width="5.2" height="9.6" rx="2.6" />
      <path d="M4.6 9.4a5.4 5.4 0 0 0 10.8 0M10 14.8v2.8M7.2 17.6h5.6" />
    </>
  ),
  factura: (
    <>
      <path d="M4.4 2.8h11.2v14.4l-2.2-1.3-2.2 1.3-2.2-1.3-2.2 1.3-2.4-1.3V2.8Z" />
      <path d="M7.2 6.6h5.6M7.2 9.8h5.6M7.2 13h3.2" />
    </>
  ),
  candado: (
    <>
      <rect x="3.8" y="8.6" width="12.4" height="8.4" rx="1.8" />
      <path d="M6.6 8.6V6.4a3.4 3.4 0 0 1 6.8 0v2.2" />
    </>
  ),
  enchufe: (
    <>
      <path d="M7 2.6v4.4M13 2.6v4.4" />
      <path d="M4.6 7h10.8v2.6a5.4 5.4 0 0 1-10.8 0V7Z" />
      <path d="M10 15v2.4" />
    </>
  ),
  paleta: (
    <>
      <path d="M10 2.8a7.2 7.2 0 1 0 0 14.4c1 0 1.5-.7 1.5-1.4 0-.8-.7-1.3-.7-2 0-.7.6-1.2 1.4-1.2h1.4a3.6 3.6 0 0 0 3.6-3.6c0-3.4-3.2-6.2-7.2-6.2Z" />
      <circle cx="6.6" cy="9" r="1" fill="currentColor" stroke="none" />
      <circle cx="9.4" cy="6.4" r="1" fill="currentColor" stroke="none" />
      <circle cx="13" cy="7.4" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  etiqueta: (
    <>
      <path d="M2.8 9.6V3.4a.6.6 0 0 1 .6-.6h6.2l7 7-6.8 6.8-7-7Z" />
      <circle cx="6.4" cy="6.4" r="1.2" />
    </>
  ),
  edificio: (
    <>
      <path d="M3.6 17.2V4.4l7-1.8v14.6M10.6 8h5.8v9.2M2.4 17.2h15.2" />
      <path d="M6 7.4h1.6M6 10.4h1.6M6 13.4h1.6M12.8 11h1.4M12.8 14h1.4" />
    </>
  ),
  papelera: (
    <>
      <path d="M3.4 5.4h13.2M8 5.4V3.6h4v1.8M5.4 5.4l.8 11.2h7.6l.8-11.2" />
      <path d="M8.4 8.6v5M11.6 8.6v5" />
    </>
  ),
  lapiz: (
    <>
      <path d="M13.2 3 17 6.8 7 16.8l-4.4.6.6-4.4L13.2 3Z" />
      <path d="m11.6 4.6 3.8 3.8" />
    </>
  ),
  comentario: <path d="M3 4.4h14v9.2h-8l-4 3.4v-3.4H3V4.4Z" />,
  prohibido: (
    <>
      <circle cx="10" cy="10" r="7.2" />
      <path d="m5 5 10 10" />
    </>
  ),
  enviar: <path d="M17.4 2.6 2.6 8.2l6 2.4 2.4 6 6.4-14Zm0 0-8.8 8" />,
  descargar: (
    <>
      <path d="M10 2.8v9.6M6.4 9l3.6 3.4L13.6 9" />
      <path d="M3.4 13.4v2.4a1.4 1.4 0 0 0 1.4 1.4h10.4a1.4 1.4 0 0 0 1.4-1.4v-2.4" />
    </>
  ),
  repetir: (
    <>
      <path d="M3.2 8.6a6.9 6.9 0 0 1 11.6-2.8l2 2M16.8 11.4a6.9 6.9 0 0 1-11.6 2.8l-2-2" />
      <path d="M16.8 3.6v4.2h-4.2M3.2 16.4v-4.2h4.2" />
    </>
  ),
  pin: (
    <>
      <path d="M10 17.4s5.4-5 5.4-9a5.4 5.4 0 1 0-10.8 0c0 4 5.4 9 5.4 9Z" />
      <circle cx="10" cy="8.2" r="2" />
    </>
  ),
  reproducir: <path d="M6.4 3.8 16 10l-9.6 6.2V3.8Z" />,
  billete: (
    <>
      <rect x="2.2" y="5" width="15.6" height="10" rx="1.6" />
      <circle cx="10" cy="10" r="2.4" />
      <path d="M5.2 10h.01M14.8 10h.01" />
    </>
  ),
  comprobado: <path d="m4 10.4 4 4 8-9" />,
  volver: <path d="M16 10H4.4m0 0 5-5m-5 5 5 5" />,
  ojo: (
    <>
      <path d="M1.8 10S5.1 4.6 10 4.6 18.2 10 18.2 10 14.9 15.4 10 15.4 1.8 10 1.8 10Z" />
      <circle cx="10" cy="10" r="2.4" />
    </>
  ),
  'ojo-tachado': (
    <>
      <path d="M6.4 5.6A8.9 8.9 0 0 1 10 4.9c4.9 0 8.2 5.1 8.2 5.1a15 15 0 0 1-2.9 3.3M4.6 6.8A15.4 15.4 0 0 0 1.8 10s3.3 5.1 8.2 5.1a8.6 8.6 0 0 0 2.6-.4" />
      <path d="M8.3 8.3a2.4 2.4 0 0 0 3.4 3.4" />
      <path d="m3.4 3.4 13.2 13.2" />
    </>
  ),
};

export function Icono({ nombre }: { nombre: NombreIcono }) {
  return (
    <svg
      className="icono"
      viewBox="0 0 20 20"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {TRAZOS[nombre]}
    </svg>
  );
}
